import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Faq, ProjectSummary, Service } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowDownRight, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { revealDelay } from '@/lib/motion'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaLink } from '@/components/generals/cta-link'
import { Mark } from '@/components/generals/mark'
import { SectionLabel } from '@/components/generals/section-label'
import PageHero from '@/components/sections/page-hero'
import ProcessSection from '@/components/sections/process-section'
import ProjectGrid from '@/components/sections/project-grid'
import FaqSection from '@/components/sections/faq-section'
import FinalCta from '@/components/sections/final-cta'

import {
    REVALIDATE_SECONDS,
    getFaqs,
    getProjects,
    getServices
} from '@/lib/content'

interface ServicesPageProps {
    services: Service[]
    projects: ProjectSummary[]
    faqs: Faq[]
}

const ServicesPage: FC<ServicesPageProps> = ({ services, projects, faqs }): ReactNode => {
    return (
        <Layout>
            <Seo title='Services' description='Marketing strategy, brand identity, digital advertising, performance marketing, creative campaigns and marketing analytics from BLITZ.' />

            <PageHero
                id='services-hero'
                tone='stone'
                label='Services'
                meta={services.length > 0 ? `${pad(services.length)} disciplines` : undefined}
                lines={[
                    'Creative strategy.',
                    <Mark key='growth'>Measurable growth.</Mark>
                ]}
                lead={services.length > 0 ? (
                    <span aria-hidden='true' className='text-outline-charcoal block font-heading font-wide text-[clamp(3.5rem,6vw,5.5rem)] font-black leading-[0.8] tracking-[-0.05em]'>
                        {pad(services.length)}
                    </span>
                ) : undefined}
                intro='From positioning your brand to executing campaigns and optimizing results, we create marketing solutions designed around your business objectives.'
                actions={<CtaLink href='/contact' variant='dark'>Start a project</CtaLink>}
            >
                {services.length > 0 && (
                    <nav aria-label='Services on this page' className='reveal-fade mt-14 md:mt-16' style={revealDelay(560)}>
                        <ol className='no-scrollbar -mx-5 flex snap-x gap-4 overflow-x-auto px-5 pb-2 sm:mx-0 sm:grid sm:grid-cols-3 sm:gap-x-6 sm:gap-y-8 sm:overflow-visible sm:px-0 sm:pb-0 xl:grid-cols-6'>
                            {services.map((service, index) => (
                                <li key={service.id} className='w-[42vw] max-w-[12rem] shrink-0 snap-start sm:w-auto sm:max-w-none'>
                                    <a
                                        href={`#${service.slug}`}
                                        className='group block rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-charcoal'
                                    >
                                        <span className='relative block aspect-[4/5] overflow-hidden bg-cream/60'>
                                            {service.image && (
                                                <Image
                                                    src={service.image}
                                                    alt=''
                                                    fill
                                                    sizes='(min-width: 1280px) 15vw, (min-width: 640px) 30vw, 45vw'
                                                    className='object-cover transition-[scale,filter] duration-[1200ms] ease-[var(--ease-out-expo)] grayscale-[35%] group-hover:scale-[1.05] group-hover:grayscale-0'
                                                />
                                            )}
                                            <span className='absolute left-0 top-0 bg-cream px-2 py-1 font-mono text-[10px] text-charcoal'>
                                                {pad(index + 1)}
                                            </span>
                                            <span aria-hidden='true' className='absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-coral transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:scale-x-100' />
                                        </span>
                                        <span className='mt-3 flex items-start justify-between gap-3'>
                                            <span className='font-heading font-wide text-[13px] font-black uppercase leading-tight tracking-[-0.01em] md:text-sm'>
                                                {service.shortTitle}
                                            </span>
                                            <ArrowDownRight aria-hidden='true' className='size-4 shrink-0 transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:translate-x-0.5 group-hover:translate-y-0.5' />
                                        </span>
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </nav>
                )}
            </PageHero>

            {services.map((service, index) => {
                const reversed = index % 2 === 1

                return (
                    <section
                        key={service.id}
                        id={service.slug}
                        aria-labelledby={`${service.slug}-title`}
                        data-nav-theme='light'
                        className={cn('scroll-mt-20 text-charcoal', reversed ? 'bg-sand' : 'bg-cream')}
                    >
                        <div className='mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12 lg:items-center lg:gap-10'>
                            <div className={cn('relative lg:col-span-5', reversed && 'lg:order-2 lg:col-start-8')}>
                                <span aria-hidden='true' className='text-outline-charcoal block font-heading font-wide text-[clamp(3.5rem,7vw,6rem)] font-black leading-[0.8] tracking-[-0.05em]'>
                                    {pad(index + 1)}
                                </span>
                                <div className='relative mt-6'>
                                    <div aria-hidden='true' className={cn('absolute -bottom-4 h-1/2 w-1/2 bg-coral', reversed ? '-left-4' : '-right-4')} />
                                    <div data-reveal-image className='relative aspect-[4/3] overflow-hidden bg-stone'>
                                        {service.image && (
                                            <Image src={service.image} alt={service.imageAlt} fill sizes='(min-width: 1024px) 40vw, 100vw' className='object-cover' />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={cn('lg:col-span-6', reversed ? 'lg:order-1 lg:col-start-1' : 'lg:col-start-7')}>
                                <div data-reveal>
                                    <SectionLabel>Service / {pad(index + 1)}</SectionLabel>
                                    <h2 id={`${service.slug}-title`} className='mt-6 font-heading type-section'>
                                        {service.title}
                                    </h2>
                                    <p className='mt-6 text-[17px] leading-relaxed text-charcoal/85'>{service.description || service.summary}</p>
                                </div>

                                <div className='mt-10 grid gap-10 sm:grid-cols-2'>
                                    {service.challenges.length > 0 && (
                                        <div data-reveal>
                                            <h3 className='font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/80'>Business challenges we solve</h3>
                                            <ul className='mt-4 space-y-3 border-t border-charcoal pt-4'>
                                                {service.challenges.map((challenge) => (
                                                    <li key={challenge} className='flex gap-3 text-[15px] leading-snug'>
                                                        <ArrowRight aria-hidden='true' className='mt-0.5 size-4 shrink-0' />
                                                        {challenge}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {service.deliverables.length > 0 && (
                                        <div data-reveal>
                                            <h3 className='font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/80'>Deliverables</h3>
                                            <ul className='mt-4 flex flex-wrap gap-2 border-t border-charcoal pt-4'>
                                                {service.deliverables.map((deliverable) => (
                                                    <li key={deliverable} className='rounded-sm border border-charcoal/30 px-3 py-1.5 text-[13px] font-medium'>
                                                        {deliverable}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                <div data-reveal className='mt-10'>
                                    <CtaLink href={`/services/${service.slug}`} variant='dark'>
                                        Explore {service.shortTitle}
                                    </CtaLink>
                                </div>
                            </div>
                        </div>
                    </section>
                )
            })}

            <ProcessSection label='How we work' />

            {projects.length > 0 && (
                <section aria-labelledby='examples-title' data-nav-theme='dark' className='bg-charcoal text-cream'>
                    <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                        <div data-reveal className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                            <div>
                                <SectionLabel tone='dark'>Project examples</SectionLabel>
                                <h2 id='examples-title' className='mt-6 font-heading type-section'>
                                    Services <Mark tone='dark'>in action.</Mark>
                                </h2>
                            </div>
                            <Link href='/work' className='inline-flex items-center gap-2 border-b border-cream pb-1 text-[13px] font-semibold uppercase tracking-[0.08em] hover:text-coral'>
                                All projects
                                <ArrowRight aria-hidden='true' className='size-4' />
                            </Link>
                        </div>
                        <ProjectGrid projects={projects.slice(0, 2)} className='mt-14' />
                    </div>
                </section>
            )}

            <FaqSection faqs={faqs} label='FAQ' />
            <FinalCta />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ServicesPageProps> = async () => {
    const [services, projects, faqs] = await Promise.all([getServices(), getProjects(), getFaqs()])

    return {
        props: { services, projects, faqs },
        revalidate: REVALIDATE_SECONDS
    }
}

export default ServicesPage
